/**
 * A "a write just succeeded" signal for views whose data can be changed from
 * anywhere in the app.
 *
 * A long-lived view (the capacity timeline) can't tell that a work item was
 * rescheduled by a modal on top of it, or by the same user in another tab — it
 * has no reason to re-read. Route changes are covered by the view's own
 * remount, but a write that happens while the view stays mounted isn't
 * (#3712). The HTTP layer publishes every successful mutation here, and
 * interested views subscribe and decide for themselves whether the URL matters.
 *
 * Deliberately dumb: no payloads, no cache keys, no coupling to any store —
 * just method + url, so `services/` stays free of feature knowledge.
 */
export interface ApiWriteEvent {
  /** Lower-case HTTP verb (`post` / `put` / `patch` / `delete`). */
  method: string;
  /** Request URL as passed to the client, e.g. `/api/workspaces/acme/events/`. */
  url: string;
}

type ApiWriteListener = (event: ApiWriteEvent) => void;

const listeners = new Set<ApiWriteListener>();

/** Subscribe to successful mutations. Returns the unsubscribe function. */
export function subscribeToApiWrites(listener: ApiWriteListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Announce a successful mutation. Never throws: a listener that fails must not
 * turn a completed write into a rejected request for the caller.
 */
export function publishApiWrite(event: ApiWriteEvent): void {
  for (const listener of listeners) {
    try {
      listener(event);
    } catch (error) {
      console.error("[api] an api-write listener threw", { event, error });
    }
  }
}
